import { memo } from "react"

import { CellProps, useFocusManagement } from "components/TreeState"
import { ChangedPlanRecord, NumericWithUnit } from "../types"
import { css, cx } from "@emotion/css"

import { Bulky } from "./Bulky"
import { CellError } from "./CellError"
import { Info } from "./Info"
import Input from "ui/inputs/Input"
import { formatFloatingPointNumber } from "utils/number-format"
import { get } from "lodash"
import { tableAggregations } from "../components/AutoCompute/useAggregations"
import { useAtomValue } from "jotai/utils"
import { useBillingTypesRules } from "../useBillingTypeRules"
import { useSnapshot } from "valtio"
import { useTableSource } from "../TableStateProvider"
import { DEFAULT_PRECISION, usePrecision } from "providers/A12n"

export const PreciseNumericWithUnitCell = memo(
  function _PreciseNumericWithUnitCell(props: CellProps) {
    const precision = usePrecision()
    return <NumericWithUnitCell {...props} precision={precision} />
  },
)

export function NumericWithUnitCell(props: CellProps) {
  const {
    rowId: id,
    dataKey,
    columnId,
    format,
    primaryKey,
    semantic,
    precision = DEFAULT_PRECISION,
  } = props
  const percentage = format === "PERCENTAGE"
  const tableSource = useTableSource()
  const aggregations = useAtomValue(tableAggregations)
  const readFrom = useSnapshot(tableSource.state)
  const readError = useSnapshot(tableSource.errors)

  const readonly = true
  const type = readFrom[id]?.type
  const packaged = readFrom[id]?.packaged
  const isMediaSubmedia =
    type === "media_type_plan" || type === "media_subtype_plan"

  const isBooking = primaryKey === "buyingMediaFields"
  const error = get(readError, `${id}.${columnId}`)
  const aggregatedValue = get(aggregations, [id, columnId]) ?? ""
  const hasAggregation = !!Object.keys(aggregations).includes(id)
  const { value = "", unit = "" } = (get(readFrom, `${id}.${dataKey}`) ||
    {}) as NumericWithUnit
  const appliedValue =
    isMediaSubmedia && hasAggregation && !isBooking && !packaged
      ? (aggregatedValue ?? value)
      : value

  const formattedValue = formatFloatingPointNumber(precision, 0)(appliedValue)

  const { getInputProps } = useFocusManagement()
  const { onBlur } = getInputProps()

  // apply billing type rules
  const viewable = useBillingTypesRules(
    readFrom[id] as ChangedPlanRecord,
    primaryKey,
    semantic,
  )
  if (!viewable) return <span />

  return (
    <>
      <Bulky {...props} otherDisabled={!!readonly}>
        <div className={styles.primaryInput}>
          <div className={cx("BodyBody-xsDark-greyRegular", styles.unit)}>
            {unit}
          </div>
          <Input
            readOnly
            onBlur={onBlur}
            value={formattedValue}
            style={{ textAlign: "end" }}
            autoComplete="off"
            type="tel"
            className={styles.input}
          />
          {percentage ? <span>%</span> : null}
        </div>
      </Bulky>
      <Info {...props} />
      {!!error ? <CellError error={error} /> : null}
    </>
  )
}
const styles = {
  input: css`
    text-align: end;
  `,
  title: css`
    min-width: 64px;
  `,
  unit: css`
    text-align: end;
    width: 100%;
  `,
  primaryInput: css`
    display: flex;
    justify-content: end;
    align-items: center;
    width: 100%;
    font-size: 13px;
  `,
}
